import { useState } from "react";
import { toast } from "sonner";
import { undoToast } from "@/components/admin/UndoButton";
import { API } from "@/lib/api";
import { readJson } from "./adminApi";

const EMPTY_REG = { name: "", mobile: "", email: "", profession: "", place: "", program: "PositiveLife", batch: "" };
const DEFAULT_COLS = ["sno", "name", "wa_name", "mobile", "email", "profession", "place", "program", "batch", "checklist_agreed", "checklist_agreed_at", "created_at"];

export const useRegistrations = (key, authFetch, onUndone) => {
  const [rows, setRows] = useState([]);
  const [agreedFilter, setAgreedFilter] = useState("all");
  const [selected, setSelected] = useState([]);
  const [regLimit, setRegLimit] = useState(3);
  const [limitInput, setLimitInput] = useState(3);
  const [savingLimit, setSavingLimit] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [addingReg, setAddingReg] = useState(false);
  const [regForm, setRegForm] = useState(EMPTY_REG);
  const [savingReg, setSavingReg] = useState(false);
  const [exportCols, setExportCols] = useState(DEFAULT_COLS);
  const [exportSelectedOnly, setExportSelectedOnly] = useState(false);
  const [archiving, setArchiving] = useState(false);

  const visibleRows = rows.filter((r) =>
    agreedFilter === "all" ? true : agreedFilter === "yes" ? !!r.checklist_agreed : !r.checklist_agreed
  );

  const hydrate = (data) => { setRows(data); setSelected([]); };
  const hydrateLimit = (limit) => { setRegLimit(limit); setLimitInput(limit); };

  const toggleSelect = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAll = () =>
    setSelected((s) => {
      const visIds = visibleRows.map((r) => r.id);
      const allSelected = visIds.length > 0 && visIds.every((id) => s.includes(id));
      return allSelected ? s.filter((id) => !visIds.includes(id)) : [...new Set([...s, ...visIds])];
    });

  const deleteSelected = async () => {
    if (selected.length === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selected.length} selected registration(s)? (You can Undo afterwards.)`)) return;
    try {
      const res = await authFetch(`/admin/registrations/bulk-delete`, { method: "POST", body: JSON.stringify({ ids: selected }) });
      const data = await readJson(res);
      if (!res.ok) throw new Error(data?.detail || "Failed");
      setRows((rs) => rs.filter((r) => !selected.includes(r.id)));
      setSelected([]);
      undoToast(authFetch, `Deleted ${data.deleted} registration(s)`, data, () => onUndone?.());
    } catch {
      toast.error("Bulk delete failed");
    }
  };

  const saveLimit = async () => {
    const val = parseInt(limitInput, 10);
    if (isNaN(val) || val < 0) { toast.error("Enter a valid number"); return; }
    setSavingLimit(true);
    try {
      const res = await authFetch(`/admin/settings`, { method: "PUT", body: JSON.stringify({ reg_limit: val }) });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setRegLimit(data.reg_limit);
      toast.success(`Maximum registrations set to ${data.reg_limit}`);
    } catch {
      toast.error("Failed to update limit");
    } finally {
      setSavingLimit(false);
    }
  };

  const copyWaNames = async () => {
    if (rows.length === 0) { toast.error("No registrations to copy"); return; }
    const list = rows.map((r, idx) => `Lp${(r.batch || "").trim()} A${idx + 1} ${(r.name || "").trim()}`.trim());
    const text = list.join("\n");
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`Copied ${list.length} WhatsApp name(s)`);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); toast.success(`Copied ${list.length} WhatsApp name(s)`); }
      catch { toast.error("Copy failed — please copy manually"); }
      ta.remove();
    }
  };

  const startEdit = (r) => {
    setEditing(r);
    setEditForm({ name: r.name || "", mobile: r.mobile || "", whatsapp: r.whatsapp || "", email: r.email || "", profession: r.profession || "", place: r.place || "", program: r.program || "", batch: r.batch || "" });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const isNew = editing?.id === "new";
    try {
      const res = await authFetch(isNew ? `/admin/registrations` : `/admin/registrations/${editing.id}`, { method: isNew ? "POST" : "PUT", body: JSON.stringify(editForm) });
      const updated = await readJson(res);
      if (!res.ok) throw new Error(updated?.detail || "Failed");
      setRows((rs) => (isNew ? [...rs, updated] : rs.map((x) => (x.id === updated.id ? updated : x))));
      undoToast(authFetch, isNew ? "Record added to Future Batch" : "Registration updated", updated, () => onUndone?.());
      setEditing(null);
    } catch (err) {
      toast.error(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const deleteOne = async (r) => {
    if (!window.confirm(`Are you sure you want to delete ${r.name}'s registration? (You can Undo afterwards.)`)) return;
    try {
      const res = await authFetch(`/admin/registrations/${r.id}`, { method: "DELETE" });
      const data = await readJson(res);
      if (!res.ok) throw new Error(data?.detail || "Failed");
      setRows((rs) => rs.filter((x) => x.id !== r.id));
      undoToast(authFetch, "Registration deleted", data, () => onUndone?.());
    } catch {
      toast.error("Delete failed");
    }
  };

  const emptySelected = async () => {
    if (selected.length === 0) return;
    try {
      const res = await authFetch(`/admin/registrations/bulk-delete`, { method: "POST", body: JSON.stringify({ ids: selected }) });
      const data = await readJson(res);
      if (!res.ok) throw new Error(data?.detail || "Failed");
      setRows((rs) => rs.filter((r) => !selected.includes(r.id)));
      setSelected([]);
      undoToast(authFetch, `Deleted ${data.deleted} selected registration(s)`, data, () => onUndone?.());
    } catch (err) {
      toast.error(err.message || "Failed to delete the selected registrations");
    }
  };

  const archiveSelected = async (onArchived) => {
    if (selected.length === 0) return;
    setArchiving(true);
    try {
      const res = await authFetch(`/admin/past-participants/archive`, { method: "POST", body: JSON.stringify({ ids: selected }) });
      const data = await readJson(res);
      if (!res.ok) throw new Error(data?.detail || "Archive failed");
      setRows((rs) => rs.filter((r) => !selected.includes(r.id)));
      setSelected([]);
      undoToast(authFetch, `Archived ${data.archived} participant(s) to Past Batch and removed ${data.removed} from the list. Past total: ${data.past_total}`, data, () => onUndone?.());
      onArchived?.();
    } catch (err) {
      toast.error(err.message || "Archive failed");
    } finally {
      setArchiving(false);
    }
  };

  const openAddRegistration = (batchNo) => {
    setRegForm((rf) => ({ ...rf, batch: batchNo || "" }));
    setAddingReg(true);
  };

  const createRegistration = async (e) => {
    e.preventDefault();
    if (!regForm.name.trim()) { toast.error("Name is required"); return; }
    setSavingReg(true);
    try {
      const res = await authFetch(`/admin/registrations`, { method: "POST", body: JSON.stringify(regForm) });
      const data = await readJson(res);
      if (!res.ok) throw new Error(data?.detail || "Failed");
      setRows((rs) => [data, ...rs]);
      undoToast(authFetch, "Registration added", data, () => onUndone?.());
      setAddingReg(false);
      setRegForm(EMPTY_REG);
    } catch (err) {
      toast.error(err.message || "Failed to add registration");
    } finally {
      setSavingReg(false);
    }
  };

  const toggleCol = (c) =>
    setExportCols((cols) => (cols.includes(c) ? cols.filter((x) => x !== c) : [...cols, c]));

  const runExport = async (fmt) => {
    if (exportCols.length === 0) { toast.error("Select at least one column"); return; }
    const params = new URLSearchParams({ fmt, columns: exportCols.join(",") });
    if (exportSelectedOnly) {
      if (selected.length === 0) { toast.error("No rows selected"); return; }
      params.set("ids", selected.join(","));
    } else if (agreedFilter !== "all") {
      params.set("agreed", agreedFilter);
    }
    try {
      const res = await fetch(`${API}/admin/registrations/export?${params.toString()}`, { headers: { "X-PL-Client": "web", "X-Admin-Key": key } });
      if (!res.ok) throw new Error();
      const url = window.URL.createObjectURL(await res.blob());
      const a = document.createElement("a");
      a.href = url;
      a.download = `positivelife_registrations.${fmt === "csv" ? "csv" : "xlsx"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      const suffix = !exportSelectedOnly && agreedFilter !== "all" ? ` (${agreedFilter === "yes" ? "Agreed only" : "Not agreed only"})` : "";
      toast.success(`Exported ${fmt.toUpperCase()}${suffix}`);
    } catch {
      toast.error("Export failed");
    }
  };


  const [uploadingRegs, setUploadingRegs] = useState(false);
  const uploadCsv = async (e, onDone) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploadingRegs(true);
    try {
      const res = await authFetch(`/admin/registrations/upload`, { method: "POST", body: fd, headers: {} });
      const data = await readJson(res);
      if (!res.ok) throw new Error(data?.detail || "Upload failed");
      undoToast(authFetch, `Uploaded ${data.added} registration(s) · skipped: ${data.skipped} · already registered: ${data.duplicates}`, data, () => onUndone?.());
      onDone?.();
    } catch (err) {
      toast.error(err.message || "Upload failed", { duration: 8000 });
    } finally { setUploadingRegs(false); }
  };

  return {
    rows, setRows, visibleRows, agreedFilter, setAgreedFilter, hydrate, hydrateLimit,
    selected, toggleSelect, toggleAll, deleteSelected, uploadCsv, uploadingRegs,
    regLimit, limitInput, setLimitInput, saveLimit, savingLimit, copyWaNames,
    editing, setEditing, editForm, setEditForm, startEdit, saveEdit, saving, deleteOne, emptySelected, archiveSelected, archiving,
    addingReg, setAddingReg, regForm, setRegForm, openAddRegistration, createRegistration, savingReg,
    exportCols, toggleCol, exportSelectedOnly, setExportSelectedOnly, runExport, exportExcel: () => runExport("xlsx"),
  };
};
